import {useState, useEffect, useMemo} from "react"
import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch,
    FormControlLabel, TextField, Snackbar, Alert, Chip
} from "@mui/material"
import {
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    Schedule as ScheduleIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next";
import axiosInstance from "../config/axios-config";

const dayKeys = {
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
}

const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    return `${hours}:${minutes}`;
}

// Convert JS day (0=Sunday) to our format (1=Monday, 7=Sunday)
const getAdjustedDay = (jsDay) => {
    return jsDay === 0 ? 7 : jsDay;
}

// Parse time string to minutes since midnight
const timeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours * 60 + minutes;
}

// Format minutes to readable time string
const formatMinutesToTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else {
        return `${minutes}m`;
    }
}

export default function WorkingHours({tenant, editable = true, onUpdate, maxWidth = 500}) {
    const {t} = useTranslation();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingHours, setEditingHours] = useState([]);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [currentTime, setCurrentTime] = useState(new Date());
    // Local state to hold working hours - updates immediately after save
    const [workingHours, setWorkingHours] = useState(tenant.workingHours);

    // Sync local state when tenant prop changes
    useEffect(() => {
        setWorkingHours(tenant.workingHours);
    }, [tenant.workingHours]);

    // Update current time every minute
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // Build all 7 days, filling in missing days as closed
    // Always creates new objects to prevent reference sharing
    const getAllDays = (hours) => {
        const allDays = [];
        for (let day = 1; day <= 7; day++) {
            const existingDay = hours?.find(d => d.dayOfWeek === day);
            if (existingDay) {
                // Create a new object copy to prevent mutation
                allDays.push({
                    dayOfWeek: existingDay.dayOfWeek,
                    openTime: existingDay.openTime,
                    closeTime: existingDay.closeTime,
                    closed: Boolean(existingDay.closed),
                    open24h: Boolean(existingDay.open24h),
                    workingHoursId: existingDay.workingHoursId,
                });
            } else {
                allDays.push({
                    dayOfWeek: day,
                    openTime: "09:00",
                    closeTime: "18:00",
                    closed: true,
                    open24h: false,
                });
            }
        }
        return allDays.sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    };

    // Calculate current status
    const statusInfo = useMemo(() => {
        const now = currentTime;
        const currentDay = getAdjustedDay(now.getDay());
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const allDays = getAllDays(workingHours);
        const todaySchedule = allDays.find(d => d.dayOfWeek === currentDay);

        let isOpen = false;
        let statusText = t("Closed");
        let nextEventText = "";
        let minutesUntilNextEvent = 0;

        if (todaySchedule) {
            if (todaySchedule.open24h) {
                isOpen = true;
                statusText = t("OpenNow");
                // Find next closed day
                for (let i = 1; i <= 7; i++) {
                    const checkDay = ((currentDay - 1 + i) % 7) + 1;
                    const daySchedule = allDays.find(d => d.dayOfWeek === checkDay);
                    if (daySchedule && !daySchedule.open24h) {
                        if (daySchedule.closed) {
                            nextEventText = `${t("ClosesOn")} ${t(dayKeys[checkDay])}`;
                        } else {
                            const closeTime = formatTime(daySchedule.closeTime);
                            nextEventText = `${t("ClosesAt")} ${closeTime} ${t("on")} ${t(dayKeys[checkDay])}`;
                        }
                        break;
                    }
                }
                if (!nextEventText) {
                    nextEventText = t("Open24_7");
                }
            } else if (todaySchedule.closed) {
                isOpen = false;
                statusText = t("ClosedToday");
                // Find next open day
                for (let i = 1; i <= 7; i++) {
                    const checkDay = ((currentDay - 1 + i) % 7) + 1;
                    const daySchedule = allDays.find(d => d.dayOfWeek === checkDay);
                    if (daySchedule && !daySchedule.closed) {
                        const openTime = daySchedule.open24h ? "00:00" : formatTime(daySchedule.openTime);
                        if (i === 1) {
                            nextEventText = `${t("OpensAt")} ${openTime} ${t("tomorrow")}`;
                            minutesUntilNextEvent = (24 * 60 - currentMinutes) + timeToMinutes(daySchedule.openTime || "00:00");
                        } else {
                            nextEventText = `${t("OpensOn")} ${t(dayKeys[checkDay])} ${t("at")} ${openTime}`;
                            minutesUntilNextEvent = ((i - 1) * 24 * 60) + (24 * 60 - currentMinutes) + timeToMinutes(daySchedule.openTime || "00:00");
                        }
                        break;
                    }
                }
            } else {
                const openMinutes = timeToMinutes(todaySchedule.openTime);
                const closeMinutes = timeToMinutes(todaySchedule.closeTime);

                if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
                    isOpen = true;
                    statusText = t("OpenNow");
                    minutesUntilNextEvent = closeMinutes - currentMinutes;
                    nextEventText = `${t("ClosesIn")} ${formatMinutesToTime(minutesUntilNextEvent)}`;
                } else if (currentMinutes < openMinutes) {
                    isOpen = false;
                    statusText = t("ClosedNow");
                    minutesUntilNextEvent = openMinutes - currentMinutes;
                    nextEventText = `${t("OpensIn")} ${formatMinutesToTime(minutesUntilNextEvent)}`;
                } else {
                    isOpen = false;
                    statusText = t("ClosedNow");
                    // Find next open time
                    for (let i = 1; i <= 7; i++) {
                        const checkDay = ((currentDay - 1 + i) % 7) + 1;
                        const daySchedule = allDays.find(d => d.dayOfWeek === checkDay);
                        if (daySchedule && !daySchedule.closed) {
                            const openTime = daySchedule.open24h ? "00:00" : formatTime(daySchedule.openTime);
                            if (i === 1) {
                                nextEventText = `${t("OpensAt")} ${openTime} ${t("tomorrow")}`;
                            } else {
                                nextEventText = `${t("OpensOn")} ${t(dayKeys[checkDay])} ${t("at")} ${openTime}`;
                            }
                            break;
                        }
                    }
                }
            }
        }

        return {isOpen, statusText, nextEventText};
    }, [currentTime, workingHours, t]);

    const handleEditClick = () => {
        // Deep clone each day to prevent state mutation issues
        const days = getAllDays(workingHours).map(d => ({
            dayOfWeek: d.dayOfWeek,
            openTime: d.openTime,
            closeTime: d.closeTime,
            closed: Boolean(d.closed),
            open24h: Boolean(d.open24h),
            workingHoursId: d.workingHoursId,
        }));
        setEditingHours(days);
        setEditDialogOpen(true);
    };

    const handleDayChange = (dayOfWeek, field, value) => {
        setEditingHours(prev => prev.map(day => {
            if (day.dayOfWeek === dayOfWeek) {
                const updated = {...day, [field]: value};
                if (field === "open24h" && value === true) {
                    updated.closed = false;
                }
                if (field === "closed" && value === true) {
                    updated.open24h = false;
                }
                return updated;
            }
            return day;
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Create updated tenant object with new working hours
            const updatedTenant = {
                ...tenant,
                workingHours: editingHours
            };

            const response = await axiosInstance.put(
                `/api/v1/tenants/${tenant.id}`,
                updatedTenant
            );

            // Update local state immediately so table reflects changes
            setWorkingHours(editingHours);

            setSnackbar({open: true, message: t("WorkingHoursSaved"), severity: "success"});
            setEditDialogOpen(false);
            if (onUpdate) {
                onUpdate(response.data);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || t("ErrorSavingWorkingHours"),
                severity: "error"
            });
        } finally {
            setSaving(false);
        }
    };

    const sortedHours = getAllDays(workingHours);

    return (
        <Box>
            {editable && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <IconButton size="small" onClick={handleEditClick} color="primary">
                        <EditIcon fontSize="small"/>
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                        {t("ClickToEdit")}
                    </Typography>
                </Box>
            )}

            <Box display="flex" gap={3} alignItems="flex-start" flexWrap="wrap">
                {/* Left side - Working Hours Table */}
                <TableContainer component={Paper} variant="outlined" sx={{maxWidth, flex: "1 1 auto"}}>
                    <Table size="small">
                        <TableBody>
                            {sortedHours.map((day) => (
                                <TableRow key={day.workingHoursId || day.dayOfWeek}>
                                    <TableCell sx={{fontWeight: 500, width: 200}}>
                                        {t(dayKeys[day.dayOfWeek])}
                                    </TableCell>
                                    <TableCell>
                                        {day.closed ? (
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <CancelIcon sx={{fontSize: 16, color: "error.main"}}/>
                                                <Typography variant="body2" color="error.main">
                                                    {t("Closed")}
                                                </Typography>
                                            </Box>
                                        ) : day.open24h ? (
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <CheckCircleIcon sx={{fontSize: 16, color: "success.main"}}/>
                                                <Typography variant="body2" color="success.main">
                                                    {t("Open24Hours")}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <AccessTimeIcon sx={{fontSize: 16, color: "primary.main"}}/>
                                                <Typography variant="body2">
                                                    {formatTime(day.openTime)} - {formatTime(day.closeTime)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Right side - Today's Schedule Timeline */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2.8,
                        minWidth: 200,
                        maxWidth: 240,
                        flex: "0 0 auto",
                        borderRadius: 2,
                    }}
                >
                    {/* Header with status chip */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{fontWeight: 500}}>
                            {t(dayKeys[getAdjustedDay(currentTime.getDay())])}
                        </Typography>
                        <Chip
                            label={statusInfo.isOpen ? t("Open") : t("Closed")}
                            size="small"
                            color={statusInfo.isOpen ? "success" : "error"}
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 22,
                            }}
                        />
                    </Box>

                    {/* Timeline visualization */}
                    {(() => {
                        const todaySchedule = getAllDays(workingHours).find(
                            d => d.dayOfWeek === getAdjustedDay(currentTime.getDay())
                        );
                        const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
                        const currentPercent = (currentMinutes / (24 * 60)) * 100;

                        let openPercent = 0;
                        let closePercent = 100;
                        let isClosed = todaySchedule?.closed;
                        let is24h = todaySchedule?.open24h;

                        if (!isClosed && !is24h && todaySchedule) {
                            openPercent = (timeToMinutes(todaySchedule.openTime) / (24 * 60)) * 100;
                            closePercent = (timeToMinutes(todaySchedule.closeTime) / (24 * 60)) * 100;
                        }

                        return (
                            <Box sx={{mb: 2}}>
                                {/* Timeline bar */}
                                <Box
                                    sx={{
                                        position: "relative",
                                        height: 8,
                                        bgcolor: "grey.200",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Open hours bar */}
                                    {!isClosed && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 0,
                                                left: is24h ? 0 : `${openPercent}%`,
                                                width: is24h ? "100%" : `${closePercent - openPercent}%`,
                                                height: "100%",
                                                bgcolor: "success.main",
                                                borderRadius: 1,
                                            }}
                                        />
                                    )}
                                    {/* Current time indicator */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: -3,
                                            left: `${currentPercent}%`,
                                            transform: "translateX(-50%)",
                                            width: 14,
                                            height: 14,
                                            bgcolor: "primary.main",
                                            borderRadius: "50%",
                                            border: "2px solid white",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                        }}
                                    />
                                </Box>

                                {/* Time labels */}
                                <Box display="flex" justifyContent="space-between" mt={0.5}>
                                    <Typography variant="caption" color="text.disabled">00:00</Typography>
                                    <Typography variant="caption" color="text.disabled">24:00</Typography>
                                </Box>

                                {/* Today's hours */}
                                <Box mt={1.5} textAlign="center">
                                    {isClosed ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {t("ClosedToday")}
                                        </Typography>
                                    ) : is24h ? (
                                        <Typography variant="body2" color="success.main" sx={{fontWeight: 500}}>
                                            {t("Open24Hours")}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" sx={{fontWeight: 500}}>
                                            {formatTime(todaySchedule?.openTime)} - {formatTime(todaySchedule?.closeTime)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })()}

                    {/* Current time and next event */}
                    <Box
                        sx={{
                            pt: 2,
                            borderTop: 1,
                            borderColor: "divider",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <AccessTimeIcon sx={{fontSize: 18, color: "primary.main"}}/>
                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                {currentTime.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
                            </Typography>
                        </Box>

                        {statusInfo.nextEventText && (
                            <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>
                                {statusInfo.nextEventText}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{sx: {minHeight: "auto"}}}
            >
                <DialogTitle>{t("EditWorkingHours")}</DialogTitle>
                <DialogContent sx={{overflow: "visible", pt: 2}}>
                    <Table size="medium">
                        <TableBody>
                            {editingHours.map((day) => (
                                <TableRow key={day.dayOfWeek}
                                          sx={{'& td': {py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)'}}}>
                                    <TableCell sx={{fontWeight: 600, width: 120, fontSize: '0.95rem'}}>
                                        {t(dayKeys[day.dayOfWeek])}
                                    </TableCell>
                                    <TableCell sx={{width: 130}}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(day.closed)}
                                                    onChange={(e) => handleDayChange(day.dayOfWeek, "closed", e.target.checked)}
                                                />
                                            }
                                            label={t("Closed")}
                                        />
                                    </TableCell>
                                    <TableCell sx={{width: 160}}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(day.open24h)}
                                                    onChange={(e) => handleDayChange(day.dayOfWeek, "open24h", e.target.checked)}
                                                    disabled={Boolean(day.closed)}
                                                />
                                            }
                                            label={t("Open24Hours")}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={2} alignItems="center">
                                            <TextField
                                                type="time"
                                                value={day.openTime || "09:00"}
                                                onChange={(e) => handleDayChange(day.dayOfWeek, "openTime", e.target.value)}
                                                disabled={day.closed || day.open24h}
                                                sx={{width: 130}}
                                            />
                                            <Typography sx={{fontWeight: 500}}>-</Typography>
                                            <TextField
                                                type="time"
                                                value={day.closeTime || "18:00"}
                                                onChange={(e) => handleDayChange(day.dayOfWeek, "closeTime", e.target.value)}
                                                disabled={day.closed || day.open24h}
                                                sx={{width: 130}}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? t("Saving") : t("Save")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            >
                <Alert
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
